import type { ComponentProps, ComponentPropsWithoutRef } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { AvatarCat } from '@bice_vida/ds-components-avatar-cat';
import { useMediaQuery } from '@bice_vida/ds-react-utils';
import { Title } from '@radix-ui/react-dialog';
import { cx } from 'class-variance-authority';

import '@bice_vida/ds-styles-full-modal';

import { FullModalAsideBottomOval } from './FullModalAsideBottomOval';
import { FullModalAsideTopOval } from './FullModalAsideTopOval';

export type FullModalAsidePropsT = (
  | {
      /*
       * Defines the title of the aside.
       */
      title: string;
      /*
       * Defines the subtitle of the aside.
       */
      subtitle: string;
      /*
       * Defines the avatar icon of the aside, can not be used at the same time with icon3d.
       */
      avatar?: ComponentProps<typeof AvatarCat>['avatar'];
    }
  | {
      /*
       * Defines the title of the aside.
       */

      title: string;
      /*
       * Defines the title of the aside.
       */

      subtitle: string;
      /*
       * Can not be used at the same time with icon3d.
       */
      avatar?: never;
    }
) &
  ComponentPropsWithoutRef<'div'>;

export const FullModalAside = forwardRef<HTMLDivElement, FullModalAsidePropsT>(
  ({ title, subtitle, avatar, className, ...props }: FullModalAsidePropsT, forwardedRef) => {
    const [breakpoint, setBreakpoint] = useState('');
    const isDesktop = useMediaQuery(`(min-width: ${breakpoint})`);

    useEffect(() => {
      const breakpointValue = getComputedStyle(document.documentElement).getPropertyValue(
        '--token-breakpoints-size-m'
      );
      const legacyBreakpointValue = getComputedStyle(document.documentElement).getPropertyValue(
        '--breakpoint-m'
      );

      setBreakpoint(breakpointValue === '' ? legacyBreakpointValue : breakpointValue);
    }, [isDesktop]);

    return (
      <div {...props} className={cx('full-modal-aside', className)} ref={forwardedRef}>
        <FullModalAsideTopOval className="full-modal-aside-top" />
        <FullModalAsideBottomOval className="full-modal-aside-bottom" />

        {avatar ? (
          <AvatarCat avatar={avatar} colors="lightGrey" size={isDesktop ? 'xxl' : 'l'} />
        ) : null}

        <Title asChild>
          <div>
            <h2 className="full-modal-aside-title">{title}</h2>
            {isDesktop && <p className="text-white text-center font-regular text-paragraph/xl leading-paragraph/xl">{subtitle}</p> }
          </div>
        </Title>
      </div>
    );
  }
);

FullModalAside.displayName = 'FullModalAside';

export default FullModalAside;
